import { Action } from '@reduxjs/toolkit';
import { Epic, combineEpics } from 'redux-observable';
import {
  distinctUntilChanged,
  filter,
  from,
  map,
  mergeMap,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { intersection, isEmpty, toPairs } from 'lodash';

import module from './index';

import Product from 'domains/product';

import canvasModule, { CanvasConfiguration } from 'domains/canvases';
import subcatalogsModule from 'domains/subcatalogs';
import productsModule from 'domains/products';
import optionGroupModule from 'domains/option_groups';
import svgsModule from 'domains/svgs';

import { byId } from 'utils/common';
import { notNull } from 'utils/type_guards';

import type { RootState } from 'store/wos_store';
import type { OptionalPair, Pair, PickRequired } from 'types/common_types';
import type { SVGAPIData } from 'domains/svgs';

const { selectors } = module;

function hasCatalogId(c: CanvasConfiguration): c is PickRequired<CanvasConfiguration, 'catalog'> {
  return !!c.catalog;
}

function hasProductId(c: CanvasConfiguration): c is PickRequired<CanvasConfiguration, 'product'> {
  return !!c.product;
}

function hasOptionSelected(pair: OptionalPair<number, number>): pair is Pair<number, number> {
  return !!pair[1];
}

function isSVGIdString(s: string | SVGAPIData): s is string {
  return typeof s === 'string';
}

/**
 * Kicks off subcatalog fetch if active catalog changes
 */
const startFetchSubcatalogsEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(
      (action) =>
        canvasModule.actions.addOne.match(action) ||
        canvasModule.actions.changeActiveCanvasConfig.catalog.match(action) ||
        (canvasModule.actions.changeActiveCanvasConfig.multi.match(action) &&
          !!action.payload.catalog)
    ),
    withLatestFrom(state$),
    map(([, state]) => selectors.canvas(state)),
    filter(notNull),
    filter(hasCatalogId),
    distinctUntilChanged(
      (prev, current) => current.id === prev.id && current.catalog === prev.catalog
    ),
    map((c) => subcatalogsModule.actions.fetchSubcatalogs.pending(c.catalog))
  );

/**
 * Configure a default subcatalog when subcatalogs are fetched, if necessary
 */
const configureDefaultSubcatalogEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(subcatalogsModule.actions.fetchSubcatalogs.fulfilled.match),
    withLatestFrom(state$),
    map(([, state]) => {
      const catalogId = selectors.catalogId(state);
      if (!catalogId) return null;

      const subcatalogs = subcatalogsModule.selectors.allByCatalogId(state)[catalogId];
      if (!subcatalogs) return null;

      const currentActiveSubcatalogId = selectors.subcatalogId(state);

      // Find the default if current is not in the active catalog
      if (!subcatalogs.find((s) => s.id === currentActiveSubcatalogId)) {
        const { id } = productsModule.utils.findDefaultTemplateId(subcatalogs);
        return id;
      }

      // No configuration change required
      return null;
    }),
    filter(notNull),
    map(canvasModule.actions.changeActiveCanvasConfig.subcatalog)
  );

/**
 * Configure a default product when subcatalog changes, if necessary
 */
const configureDefaultProductEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(
      (action) =>
        (canvasModule.actions.addOne.match(action) && !action.payload.product) ||
        canvasModule.actions.changeActiveCanvasConfig.subcatalog.match(action) ||
        (canvasModule.actions.changeActiveCanvasConfig.multi.match(action) &&
          !!action.payload.subcatalog &&
          !action.payload.product)
    ),
    withLatestFrom(state$),
    map(([, state]) => {
      // Since epics run after the action has updated the redux store, the active subcatalog will be correct
      const subcatalogId = selectors.subcatalogId(state);
      if (!subcatalogId) return null;

      return subcatalogId;
    }),
    filter(notNull),
    switchMap((subcatalogId) =>
      // this sets up a second observable that emits when we have
      // products available to search for the next default,
      // and only emits once when one is found
      state$.pipe(
        map((state) => {
          const subcatalog = state.subcatalogs.entities[subcatalogId];
          return subcatalog?.templates
            .map((id) => state.products.entities[id])
            .filter(notNull)
            .filter((p) => !productsModule.utils.isCustomSizeProduct(p));
        }),
        filter(notNull),
        map(productsModule.utils.findDefaultTemplateId),
        map((found) => found.id),
        filter(notNull),
        take(1),
        map(canvasModule.actions.changeActiveCanvasConfig.product)
      )
    )
  );

/**
 * If we change to a custom size product, we need to initialize the size
 */
export const initializeCustomSizeEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(
      (action) =>
        canvasModule.actions.changeActiveCanvasConfig.product.match(action) ||
        canvasModule.actions.addOne.match(action)
    ),
    switchMap(() =>
      action$.pipe(
        withLatestFrom(state$),
        map(([, state]) => state),
        map((state) => ({
          product: selectors.product(state),
          hasSize: selectors.hasCustomSizeSet(state),
        })),
        filter(({ product, hasSize }) => {
          if (!product) return false;

          const p = new Product(product);
          return p.isCustomSize && !hasSize;
        }),
        take(1),
        map(({ product }) =>
          canvasModule.actions.changeActiveCanvasConfig.size({
            height: parseFloat(product?.print_size_y || '10'),
            width: parseFloat(product?.print_size_x || '8'),
          })
        )
      )
    )
  );

/**
 * If the product filter changes, we need to identify whether the selected product needs to
 * change as well
 */
export const productFilterChangeEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(productsModule.actions.setProductFilter.match),
    // Skip for aspect ratio, will be handled by the aspectRatioChangeEpic
    filter(({ payload }) => payload !== 'Aspect Ratio'),
    withLatestFrom(state$),
    map(([action, state]) => {
      const productData = selectors.product(state);
      if (!productData) return;

      const activeProduct = new Product(productData);

      if (activeProduct.matchesFilter(action.payload)) return;

      const subcatalog = subcatalogsModule.selectors.selectById(
        state,
        activeProduct.data.parent_id
      );

      if (!subcatalog) return;

      const filteredProducts = subcatalog.templates
        .map((id) => productsModule.selectors.selectById(state, id))
        .filter(notNull)
        .filter((product) => new Product(product).matchesFilter(action.payload));

      const { id } = productsModule.utils.findDefaultTemplateId(filteredProducts);
      return id;
    }),
    filter(notNull),
    map(canvasModule.actions.changeActiveCanvasConfig.product)
  );

/**
 * Sets the product filter when the subcatalog changes, and the current
 * filter does not apply to the selected product.
 *
 * Inverse of productFilterChangeEpic
 */
export const setDefaultProductFilterEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    withLatestFrom(state$),
    map(([, state]) => {
      const subcatalog = selectors.subcatalog(state);
      const product = selectors.product(state);
      const filter = productsModule.selectors.appliedFilter(state);

      if (!subcatalog || !product) return null;

      return { subcatalog, product, filter };
    }),
    filter(notNull),
    distinctUntilChanged((prev, curr) => prev.subcatalog.id === curr.subcatalog.id),
    filter(({ subcatalog, product }) => subcatalog.templates.includes(product.id)),
    map(({ subcatalog, product, filter }) => {
      const subcatalogFilters = subcatalog.filter_default.trim().split(',');

      // Current filter still applies, so return null and observable will be dropped
      if (subcatalogFilters.includes(filter)) return null;

      // Get the first match of the possible filters, empty string if there is no matching filter
      const p = new Product(product);
      return subcatalogFilters.find((f) => p.matchesFilter(f)) || '';
    }),
    filter(notNull),
    map(productsModule.actions.setProductFilter)
  );

/**
 * If the selected aspect ratio changes, identify if the product needs to change as well
 */
export const aspectRatioChangeEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(productsModule.actions.setAspectRatioFilter.match),
    withLatestFrom(state$),
    map(([action, state]) => {
      const productData = selectors.product(state);
      if (!productData) return;

      const activeProduct = new Product(productData);

      if (activeProduct.aspectRatioFilter === action.payload) return;

      const subcatalog = subcatalogsModule.selectors.selectById(
        state,
        activeProduct.data.parent_id
      );

      if (!subcatalog) return;

      const filteredProducts = subcatalog.templates
        .map((id) => productsModule.selectors.selectById(state, id))
        .filter(notNull)
        .filter((product) => new Product(product).aspectRatioFilter === action.payload);

      const { id } = productsModule.utils.findDefaultTemplateId(filteredProducts);
      return id;
    }),
    filter(notNull),
    map(canvasModule.actions.changeActiveCanvasConfig.product)
  );

/**
 * When the product filter is set to Aspect Ratio, set the aspect ratio
 *
 * It will set it to either the current active product's aspect ratio, otherwise the first available
 */
export const setDefaultAspectRatioEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(productsModule.actions.setProductFilter.match),
    filter(({ payload }) => payload === 'Aspect Ratio'),
    withLatestFrom(state$),
    map(([, state]) => ({
      available: selectors.aspectRatioFilters(state),
      product: selectors.product(state),
    })),
    filter(({ available }) => available.length > 0),
    map(({ available, product }) => {
      let next = (product && new Product(product).aspectRatioFilter) || available[0];

      return productsModule.actions.setAspectRatioFilter(next);
    })
  );

/**
 * Kicks off option group fetch action when the active product id changes
 */
const startFetchOptionGroupEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(
      (action) =>
        canvasModule.actions.changeActiveCanvasConfig.product.match(action) ||
        canvasModule.actions.changeActiveCanvasConfig.multi.match(action) ||
        canvasModule.actions.changeActiveCanvasConfig.priceToolSize.match(action) ||
        canvasModule.actions.addOne.match(action)
    ),
    withLatestFrom(state$),
    map(([, state]) => selectors.canvas(state)),
    filter(notNull),
    filter(hasProductId),
    distinctUntilChanged((prev, current) => {
      return current.id === prev.id && current.product === prev.product;
    }),
    map((c) => optionGroupModule.actions.fetchOptionGroups.pending(c.product))
  );

/**
 * Configure default option selections when option groups are fetched.
 *
 * Retain any selected option ids if those exist in a new option group
 */
export const configureDefaultOptionsEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(optionGroupModule.actions.fetchOptionGroups.fulfilled.match),
    withLatestFrom(state$),
    map(([action, state]) => {
      const { optionGroups } = action.payload;
      const productConfig = selectors.canvas(state);

      if (!productConfig) {
        return null;
      }

      return optionGroupModule.utils.configureDefaultOptions(optionGroups, productConfig.options);
    }),
    filter(notNull),
    map((configured) => toPairs(configured)),
    map((options) =>
      canvasModule.actions.changeActiveCanvasConfig.options({ options, replace: true })
    )
  );

/**
 * Watch for changes to a canvas's options, and ensure applied option group filters match
 * the selected option
 */
const updateAppliedOptionGroupFilters: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(canvasModule.actions.changeActiveCanvasConfig.options.match),
    withLatestFrom(state$),
    map(([{ payload }, state]) => {
      const optionGroupsById = state.optionGroups.entities;

      return payload.options
        .filter(hasOptionSelected)
        .reduce((acc: Record<number, string>, [ogid, oid]) => {
          const optionGroup = optionGroupsById[ogid];
          const option = optionGroup?.options.find(byId(oid));

          if (!optionGroup || !option) {
            return acc;
          }

          const availableFilters = optionGroup.filter_default.trim().split(',');
          const optionFilters = option.filter_group.trim().split(',');
          const filter = intersection(availableFilters, optionFilters)[0];

          if (filter) {
            acc[optionGroup.id] = filter;
          }

          return acc;
        }, {});
    }),
    filter((appliedFilters) => !isEmpty(appliedFilters)),
    map(optionGroupModule.actions.setAppliedOptionGroupFilters)
  );

/** Kick off the fetch of an SVG image if we have an unfetched svg_id on an active node */
const startSVGImageFetchEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  state$.pipe(
    map(selectors.nodes),
    mergeMap((n) => from(n)),
    map((n) => n.svg_id),
    filter(notNull),
    withLatestFrom(state$),
    map(([svgId, state]) => svgsModule.selectors.selectById(state, svgId) ?? svgId),
    filter(isSVGIdString),
    distinctUntilChanged(),
    map(svgsModule.actions.getSVGImage.pending)
  );

const combined = combineEpics(
  startFetchSubcatalogsEpic,
  configureDefaultSubcatalogEpic,
  configureDefaultProductEpic,
  initializeCustomSizeEpic,
  productFilterChangeEpic,
  aspectRatioChangeEpic,
  setDefaultAspectRatioEpic,
  setDefaultProductFilterEpic,
  startFetchOptionGroupEpic,
  configureDefaultOptionsEpic,
  updateAppliedOptionGroupFilters,
  startSVGImageFetchEpic
);
export default combined;
